/**
 * selected-workspace-storage.ts
 *
 * Browser helpers for persisting the current workspace selection.
 *
 * Primary responsibilities:
 * - Read the canonical selected workspace id from localStorage
 * - Migrate legacy selected workspace storage values
 * - Clear stale workspace selection keys on sign-out
 *
 * Dependencies:
 * - Browser localStorage
 */

const SELECTED_WORKSPACE_ID_STORAGE_KEY = 'selectedWorkspaceId'
const LEGACY_SELECTED_WORKSPACE_STORAGE_KEY = 'selectedWorkspace'
const SELECTED_WORKSPACE_ID_USER_PREFIX = `${SELECTED_WORKSPACE_ID_STORAGE_KEY}:`

// --------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// --------------

function normalizeWorkspaceId(value: string | null) {
  if (typeof value !== 'string') {
    return null
  }

  const normalizedValue = value.trim()
  return normalizedValue.length > 0 ? normalizedValue : null
}

// --------------

function normalizeUserId(value: string | null | undefined) {
  if (typeof value !== 'string') {
    return null
  }

  const normalizedValue = value.trim()
  return normalizedValue.length > 0 ? normalizedValue : null
}

// --------------

function getUserScopedWorkspaceStorageKey(userId: string | null | undefined) {
  const normalizedUserId = normalizeUserId(userId)
  return normalizedUserId ? `${SELECTED_WORKSPACE_ID_USER_PREFIX}${normalizedUserId}` : null
}

// --------------

function getScopedWorkspaceStorageKeys(storage: Storage, userId?: string | null) {
  const userScopedKey = getUserScopedWorkspaceStorageKey(userId)

  if (userScopedKey) {
    return [userScopedKey]
  }

  const keys: string[] = []

  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index)

    if (key?.startsWith(SELECTED_WORKSPACE_ID_USER_PREFIX)) {
      keys.push(key)
    }
  }

  return keys
}

// --------------

function readLegacyWorkspaceId(rawValue: string | null) {
  const normalizedRawValue = normalizeWorkspaceId(rawValue)
  if (!normalizedRawValue) {
    return null
  }

  try {
    const parsedValue = JSON.parse(normalizedRawValue)

    if (typeof parsedValue === 'string') {
      return normalizeWorkspaceId(parsedValue)
    }

    if (isRecord(parsedValue) && typeof parsedValue.id === 'string') {
      return normalizeWorkspaceId(parsedValue.id)
    }
  } catch {
    return normalizedRawValue
  }

  return null
}

// --------------

export function readSelectedWorkspaceIdFromStorage(userId?: string | null) {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const userScopedWorkspaceKey = getUserScopedWorkspaceStorageKey(userId)

    if (userScopedWorkspaceKey) {
      const persistedUserScopedWorkspaceId = normalizeWorkspaceId(
        window.localStorage.getItem(userScopedWorkspaceKey)
      )

      if (persistedUserScopedWorkspaceId) {
        return persistedUserScopedWorkspaceId
      }
    }

    const persistedWorkspaceId = normalizeWorkspaceId(
      window.localStorage.getItem(SELECTED_WORKSPACE_ID_STORAGE_KEY)
    )

    if (persistedWorkspaceId) {
      if (userScopedWorkspaceKey) {
        window.localStorage.setItem(userScopedWorkspaceKey, persistedWorkspaceId)
        window.localStorage.removeItem(SELECTED_WORKSPACE_ID_STORAGE_KEY)
      }

      return persistedWorkspaceId
    }

    const legacyWorkspaceId = readLegacyWorkspaceId(
      window.localStorage.getItem(LEGACY_SELECTED_WORKSPACE_STORAGE_KEY)
    )

    if (legacyWorkspaceId) {
      if (userScopedWorkspaceKey) {
        window.localStorage.setItem(userScopedWorkspaceKey, legacyWorkspaceId)
      } else {
        window.localStorage.setItem(SELECTED_WORKSPACE_ID_STORAGE_KEY, legacyWorkspaceId)
      }

      window.localStorage.removeItem(LEGACY_SELECTED_WORKSPACE_STORAGE_KEY)
    }

    return legacyWorkspaceId
  } catch {
    return null
  }
}

// --------------

export function persistSelectedWorkspaceIdToStorage(
  workspaceId: string | null,
  userId?: string | null
) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const normalizedWorkspaceId = normalizeWorkspaceId(workspaceId)
    const userScopedWorkspaceKey = getUserScopedWorkspaceStorageKey(userId)

    if (userScopedWorkspaceKey) {
      if (normalizedWorkspaceId) {
        window.localStorage.setItem(userScopedWorkspaceKey, normalizedWorkspaceId)
      } else {
        window.localStorage.removeItem(userScopedWorkspaceKey)
      }
    } else {
      if (normalizedWorkspaceId) {
        window.localStorage.setItem(
          SELECTED_WORKSPACE_ID_STORAGE_KEY,
          normalizedWorkspaceId
        )
      } else {
        window.localStorage.removeItem(SELECTED_WORKSPACE_ID_STORAGE_KEY)
      }
    }

    if (userScopedWorkspaceKey) {
      window.localStorage.removeItem(SELECTED_WORKSPACE_ID_STORAGE_KEY)
    }

    window.localStorage.removeItem(LEGACY_SELECTED_WORKSPACE_STORAGE_KEY)
  } catch {
    // Ignore storage failures so workspace switching still works in-memory.
  }
}

// --------------

export function clearSelectedWorkspaceIdFromStorage(userId?: string | null) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const keysToClear = getScopedWorkspaceStorageKeys(window.localStorage, userId)
    keysToClear.forEach((key) => window.localStorage.removeItem(key))

    window.localStorage.removeItem(SELECTED_WORKSPACE_ID_STORAGE_KEY)
    window.localStorage.removeItem(LEGACY_SELECTED_WORKSPACE_STORAGE_KEY)
  } catch {
    // Ignore storage failures during sign-out cleanup.
  }
}

// --------------

export {
  LEGACY_SELECTED_WORKSPACE_STORAGE_KEY,
  SELECTED_WORKSPACE_ID_STORAGE_KEY,
  SELECTED_WORKSPACE_ID_USER_PREFIX
}
